import React from "react";
import { Wifi, ArrowRight, Zap, Globe2, Check } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Button } from "./ui/button";
import { images, siteConfig } from "../mock";

const statIcons = [Zap, Globe2, Wifi];

export default function Hero() {
  const { t } = useLanguage();
  const scrollTo = (sel) => document.querySelector(sel)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative hsc-hero-bg overflow-hidden pt-[72px]">
      {/* decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,148,168,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(13,148,168,0.06) 1px, transparent 1px)",
          backgroundSize: "46px 46px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 0%, black 40%, transparent 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-20 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 pl-2 pr-3.5 py-1.5 rounded-full bg-white/70 border border-primary/15 text-primary text-[13px] font-semibold shadow-sm">
            <span className="relative flex w-2.5 h-2.5">
              <span className="hsc-ping-ring absolute inset-0 rounded-full bg-primary/60" />
              <span className="relative w-2.5 h-2.5 rounded-full bg-primary" />
            </span>
            {t.hero.badge}
          </div>

          <h1 className="mt-6 font-display font-extrabold text-slate-900 text-[40px] leading-[1.06] sm:text-[52px] lg:text-[60px] tracking-[-0.02em] whitespace-pre-line">
            {t.hero.title.split("\n")[0]}
            <span className="hsc-grad-text block">{t.hero.title.split("\n")[1]}</span>
          </h1>

          <p className="mt-6 text-[17px] leading-relaxed text-slate-700 max-w-xl">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3.5">
            <Button
              onClick={() => scrollTo("#come-funziona")}
              className="rounded-full h-12 px-7 text-[15px] font-semibold shadow-[0_14px_30px_-12px_rgba(13,148,168,0.9)] group"
            >
              {t.hero.ctaSecondary}
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
            {t.hero.stats.map((s, i) => {
              const Icon = statIcons[i];
              return (
                <div key={i} className="rounded-2xl bg-white/70 border border-white/60 p-4 hsc-card-shadow">
                  <Icon className="w-5 h-5 text-primary mb-2" strokeWidth={2.2} />
                  <div className="font-display font-bold text-slate-900 text-lg leading-none">{s.value}</div>
                  <div className="text-[12px] text-slate-500 mt-1 leading-tight">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* visual */}
        <div className="relative">
          <div className="relative hsc-float">
            <div className="absolute -inset-6 bg-gradient-to-tr from-primary/20 via-cyan-300/20 to-sky-300/20 blur-3xl rounded-[40px]" />
            <div className="relative rounded-[28px] overflow-hidden border border-white/70 shadow-[0_40px_80px_-30px_rgba(13,100,120,0.5)]">
              <img
                src={images.phoneWifi}
                alt="Connessione WiFi su smartphone"
                className="w-full h-[440px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
            </div>
          </div>

          {/* floating connected card */}
          <div className="absolute -left-3 sm:-left-6 bottom-8 hsc-glass rounded-2xl border border-white/70 p-3.5 pr-5 shadow-xl flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-600">
              <Check className="w-6 h-6" strokeWidth={2.6} />
            </span>
            <div>
              <div className="text-[11px] text-slate-500 font-medium leading-none">{siteConfig.networkName}</div>
              <div className="text-[14px] font-bold text-slate-900 mt-1 leading-none">Connected · 50 Mbit</div>
            </div>
          </div>

          {/* floating signal card */}
          <div className="absolute -right-2 sm:right-2 top-6 hsc-glass rounded-2xl border border-white/70 p-3 shadow-xl flex items-end gap-1.5">
            <Wifi className="w-5 h-5 text-primary mr-1" strokeWidth={2.4} />
            {[8, 14, 20, 26].map((h, i) => (
              <span
                key={i}
                className="w-1.5 rounded-full bg-primary"
                style={{ height: h, opacity: 0.4 + i * 0.2 }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* wave divider */}
      <div className="relative">
        <svg viewBox="0 0 1440 80" className="w-full h-[60px] block" preserveAspectRatio="none">
          <path d="M0,40 C360,90 720,0 1080,30 C1260,45 1380,55 1440,50 L1440,80 L0,80 Z" fill="#ffffff" />
        </svg>
      </div>
    </section>
  );
}
